import { useState } from 'react';
import { ApiError, api } from '../../api/client';
import { Banner, EmptyState, ErrorState, Loading } from '../../components/states';
import { useAsync } from '../../hooks/useAsync';
import { useT } from '../../i18n/I18nProvider';

interface Token {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

/** Developer section: personal/programmatic API tokens. */
export function TokensSettings(): JSX.Element {
  const t = useT();
  // The api-tokens list endpoint returns a bare array (not the paginated Page shape
  // most list endpoints use), which is the contract the e2e suite asserts.
  const { data, error, loading, reload } = useAsync(() => api.get<Token[]>('/api-tokens'), []);
  const [name, setName] = useState('');
  const [issued, setIssued] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const onCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    try {
      const res = await api.post<{ token: string }>('/api-tokens', { name });
      setIssued(res.token);
      setName('');
      reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const onRevoke = async (id: string): Promise<void> => {
    await api.del(`/api-tokens/${id}`);
    reload();
  };

  return (
    <div className="card">
      <h2>API tokens</h2>
      {issued ? (
        <Banner variant="warning">
          Copy this token now — it will not be shown again: <code>{issued}</code>
        </Banner>
      ) : null}
      <form className="form form--inline" onSubmit={(e) => void onCreate(e)}>
        {formError ? <Banner variant="error">{formError}</Banner> : null}
        <label className="field">
          <span>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="ci-deploy"
          />
        </label>
        <button type="submit" className="btn">
          {t('common.create')}
        </button>
      </form>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error.message} onRetry={reload} />
      ) : !data || data.length === 0 ? (
        <EmptyState hint="No API tokens yet." />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Last used</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {data.map((token) => (
                <tr key={token.id}>
                  <td>{token.name}</td>
                  <td>
                    <code>{token.prefix}…</code>
                  </td>
                  <td>{token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleString() : '—'}</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => void onRevoke(token.id)}
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
